import { useEffect, useMemo } from 'react';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import {
  buildSerpSellingPriceContentHtml,
  SerpSellingPriceSnapshot,
} from '@/components/Approval/@components/SerpSellingPrice';
import { getSerpSellingPriceDraftSourceUrl } from '@/constant/approval';
import { getNestedDataFetcher } from '@/utils/apis';
import type { SerpDocumentRef } from '@/utils/approval/serpDocument';
import type { SerpSellingPriceDraftSource } from '@/utils/approval/serpSellingPrice';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

type SerpSellingPriceFieldProps = {
  /** 신규 작성은 진입 쿼리, 편집은 저장 attributes 의 SERP 문서 */
  serpDocument: SerpDocumentRef | null;
};

/**
 * SERP 판매가 조정(160) — 레거시 appr_edit_forms_serp_sales.
 * 편집도 SERP 를 다시 조회해 표를 새로 그림. 제목은 직접 입력(자동 입력 없음)
 */
const SerpSellingPriceField = ({
  serpDocument,
}: SerpSellingPriceFieldProps) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'serpSellingPrice' });

  const typeCode = serpDocument?.typeCode;
  const sequence = serpDocument?.sequence;
  const { data, error, isLoading } = useSWR<SerpSellingPriceDraftSource>(
    typeCode && sequence
      ? getSerpSellingPriceDraftSourceUrl(typeCode, sequence)
      : null,
    getNestedDataFetcher,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  useEffect(() => {
    const current = getValues('serpSellingPrice');
    if (
      current &&
      (current.typeCode !== typeCode || current.sequence !== sequence)
    ) {
      setValue('serpSellingPrice', null, { shouldDirty: false });
    }
  }, [typeCode, sequence, getValues, setValue]);

  useEffect(() => {
    if (!data || !typeCode || !sequence) return;
    if (
      data.attributes.typeCode !== typeCode ||
      data.attributes.sequence !== sequence
    )
      return;
    setValue(
      'serpSellingPrice',
      { typeCode, sequence, source: data.display.source ?? {} },
      { shouldDirty: false },
    );
  }, [data, typeCode, sequence, setValue]);

  const content = useMemo(
    () =>
      state?.source ? buildSerpSellingPriceContentHtml(state.source, t) : null,
    [state?.source, t],
  );

  const renderBody = () => {
    if (!serpDocument) {
      return (
        <ApprovalNoticeBox role="alert">
          {t('SERP 문서 정보가 없습니다.')}
        </ApprovalNoticeBox>
      );
    }
    if (content) {
      return <SerpSellingPriceSnapshot content={content} />;
    }
    if (error) {
      return (
        <ApprovalNoticeBox role="alert">
          {t('데이터를 불러오지 못했습니다.')}
        </ApprovalNoticeBox>
      );
    }
    if (isLoading) {
      return (
        <ApprovalNoticeBox role="status">
          {t('데이터를 불러오는 중입니다...')}
        </ApprovalNoticeBox>
      );
    }
    return null;
  };

  return <FieldBlock className="serp-selling-price">{renderBody()}</FieldBlock>;
};

export default SerpSellingPriceField;
